/** Game HTTP health, independent of sockets and React Query cache writes. */
export interface HttpConnectionSnapshot {
  oldestRequestAt: number | null
  failureSince: number | null
}
const EMPTY: HttpConnectionSnapshot = { oldestRequestAt: null, failureSince: null }

export function gameIdForHttpPath(path: string): string | null {
  return /^\/api\/v1\/games\/([^/?]+)(?:\/|\?|$)/.exec(path)?.[1] ?? null
}

export function createHttpConnectionStore(now = () => Date.now()) {
  const games = new Map<string, {
    requests: Map<number, number>
    lastReachableRequest: number
    lastFailedRequest: number
    snapshot: HttpConnectionSnapshot
  }>()
  const listeners = new Map<string, Set<() => void>>()
  let sequence = 0
  function publish(gameId: string, entry: NonNullable<ReturnType<typeof games.get>>, failureSince: number | null) {
    const oldestRequestAt = entry.requests.size ? Math.min(...entry.requests.values()) : null
    if (entry.snapshot.oldestRequestAt === oldestRequestAt && entry.snapshot.failureSince === failureSince) return
    entry.snapshot = oldestRequestAt === null && failureSince === null ? EMPTY : { oldestRequestAt, failureSince }
    listeners.get(gameId)?.forEach(listener => listener())
  }
  return {
    snapshot: (gameId: string) => games.get(gameId)?.snapshot ?? EMPTY,
    subscribe(gameId: string, listener: () => void) {
      const group = listeners.get(gameId) ?? new Set<() => void>()
      group.add(listener)
      listeners.set(gameId, group)
      return () => { group.delete(listener); if (!group.size) listeners.delete(gameId) }
    },
    begin(path: string) {
      const gameId = gameIdForHttpPath(path)
      if (!gameId) return (_reachable: boolean) => undefined
      const request = ++sequence
      const startedAt = now()
      const entry = games.get(gameId) ?? { requests: new Map<number, number>(), lastReachableRequest: 0, lastFailedRequest: 0, snapshot: EMPTY }
      games.set(gameId, entry)
      entry.requests.set(request, startedAt)
      publish(gameId, entry, entry.snapshot.failureSince)
      let settled = false
      return (reachable: boolean) => {
        if (settled) return
        settled = true
        entry.requests.delete(request)
        let failureSince = entry.snapshot.failureSince
        if (reachable) {
          entry.lastReachableRequest = Math.max(entry.lastReachableRequest, request)
          if (request > entry.lastFailedRequest) failureSince = null
        } else if (request > entry.lastReachableRequest) {
          entry.lastFailedRequest = Math.max(entry.lastFailedRequest, request)
          failureSince = failureSince === null ? startedAt : Math.min(failureSince, startedAt)
        }
        publish(gameId, entry, failureSince)
        if (!entry.requests.size && entry.snapshot.failureSince === null) games.delete(gameId)
      }
    },
  }
}

export const httpConnection = createHttpConnectionStore()
